import React, { useState, useEffect, useRef } from 'react';
import {
    CheckCircle2, Circle, Plus, Trash2, Calendar,
    User, Briefcase, Filter, ChevronRight, X, Loader2, Flag, GripVertical,
} from 'lucide-react';
import { supabase } from '../services/supabaseService';
import { Todo } from '../types';
import TodoDetailModal from './TodoDetailModal';

interface TodoWidgetProps {
    userId: string;
    clientId?: string;
    vacancyId?: string;
    candidateId?: string;
    isOpen: boolean;
    onToggle: () => void;
    inline?: boolean;
}

interface Colleague {
    id: string;
    name: string;
    role: string;
}

const TodoWidget: React.FC<TodoWidgetProps> = ({ userId, clientId, vacancyId, candidateId, isOpen, onToggle, inline }) => {
    const [todos, setTodos] = useState<Todo[]>([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState<'all' | 'me' | 'vacancy' | 'candidate'>('all');
    const [newTodoTitle, setNewTodoTitle] = useState('');
    const [newTodoDescription, setNewTodoDescription] = useState('');
    const [newTodoDate, setNewTodoDate] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isExternal, setIsExternal] = useState(false);
    const [selectedTodoId, setSelectedTodoId] = useState<string | null>(null);

    // Explicit Form
    const [colleagues, setColleagues] = useState<Colleague[]>([]);
    const [assignedUser, setAssignedUser] = useState<Colleague | null>(null);
    const [showAssignDropdown, setShowAssignDropdown] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [priority, setPriority] = useState<'Low' | 'Medium' | 'High'>('Medium');
    const inputRef = useRef<HTMLInputElement>(null);

    /** Verticale positie van de TO DO-knop (vh, midden = 50). Opgeslagen voor hergebruik. */
    const TODO_FAB_STORAGE = 'wt_todo_fab_top_vh';
    const [fabTopVh, setFabTopVh] = useState<number>(() => {
        if (typeof window === 'undefined') return 50;
        const raw = localStorage.getItem(TODO_FAB_STORAGE);
        const n = raw != null ? Number.parseFloat(raw) : NaN;
        return Number.isFinite(n) ? Math.min(92, Math.max(8, n)) : 50;
    });
    const [fabDragging, setFabDragging] = useState(false);
    const fabTopRef = useRef(fabTopVh);
    fabTopRef.current = fabTopVh;
    useEffect(() => {
        const handleNew = () => { 
            if (!isOpen) onToggle();
            setTimeout(() => {
                setIsExpanded(true);
                inputRef.current?.focus(); 
            }, 300);
        };
        window.addEventListener('open-new-todo', handleNew);
        window.addEventListener('open-new-note', handleNew);
        return () => {
            window.removeEventListener('open-new-todo', handleNew);
            window.removeEventListener('open-new-note', handleNew);
        };
    }, [isOpen, onToggle]);

    useEffect(() => {
        if (!fabDragging) return;
        const onMove = (e: MouseEvent) => {
            const vh = (e.clientY / Math.max(window.innerHeight, 1)) * 100;
            setFabTopVh(Math.min(92, Math.max(8, vh)));
        };
        const onUp = () => {
            setFabDragging(false);
            try {
                localStorage.setItem(TODO_FAB_STORAGE, String(fabTopRef.current));
            } catch {
                /* ignore */
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [fabDragging]);

    useEffect(() => {
        if (isOpen) {
            fetchTodos();
            if (clientId) fetchColleagues();
        }
    }, [isOpen, filter, vacancyId, candidateId, clientId]);

    const fetchColleagues = async () => {
        if (!clientId) return;
        const { data } = await supabase.from('profiles').select('id, name, role').eq('client_id', clientId);
        if (data) setColleagues(data as any);
    };

    const fetchTodos = async () => {
        setLoading(true);
        try {
            let query = supabase.from('todos').select('*').order('created_at', { ascending: false });

            // Apply Filters
            if (filter === 'me') {
                query = query.eq('user_id', userId);
            } else if (filter === 'vacancy' && vacancyId) {
                query = query.eq('vacancy_id', vacancyId);
            } else if (filter === 'candidate' && candidateId) {
                query = query.eq('id', candidateId);
            } else {
                // 'all' - simplistic view: show all for user OR relevant context
                // For now, let's just show user's todos + context todos
                // Supabase OR syntax is tricky with mixed context.
                // Let's default to USER's todos if 'all', or maybe 'vacancy' if inside a vacancy.

                if (vacancyId) query = query.eq('vacancy_id', vacancyId);
                else query = query.eq('user_id', userId);
            }

            const { data, error } = await query;
            if (data) setTodos(data as Todo[]);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewTodoTitle(e.target.value);
    };

    const toggleAssignDropdown = () => {
        setShowAssignDropdown(!showAssignDropdown);
        if (showDatePicker) setShowDatePicker(false);
    };

    const toggleDatePicker = () => {
        setShowDatePicker(!showDatePicker);
        if (showAssignDropdown) setShowAssignDropdown(false);
    };

    const selectColleague = (c: Colleague | null) => {
        setAssignedUser(c);
        setShowAssignDropdown(false);
        inputRef.current?.focus();
    };

    const handleAddTodo = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTodoTitle.trim()) return;

        try {
            const { data, error } = await supabase.from('todos').insert({
                title: newTodoTitle,
                description: newTodoDescription,
                due_date: newTodoDate ? new Date(newTodoDate).toISOString() : null,
                user_id: userId,
                vacancy_id: vacancyId || null,
                candidate_id: candidateId || null,
                status: 'Pending',
                priority: priority,
                is_external: isExternal,
                assignee_id: assignedUser?.id || null,
                quick_link_url: window.location.pathname + window.location.search,
                quick_link_title: document.title || 'Werftools Pagina'
            }).select().single();

            if (data) {
                setTodos([data as Todo, ...todos]);
                setNewTodoTitle('');
                setNewTodoDescription('');
                setNewTodoDate('');
                setIsExpanded(false);
                setIsExternal(false);
                setAssignedUser(null);
                setPriority('Medium');
            }
        } catch (e) { console.error(e); }
    };

    const toggleTodo = async (todo: Todo) => {
        const newStatus = todo.status === 'Completed' ? 'Pending' : 'Completed';
        // Optimistic up
        setTodos(todos.map(t => t.id === todo.id ? { ...t, status: newStatus } : t));

        await supabase.from('todos').update({ status: newStatus }).eq('id', todo.id);
    };

    const deleteTodo = async (id: string) => {
        setTodos(todos.filter(t => t.id !== id));
        await supabase.from('todos').delete().eq('id', id);
    };

    if (!isOpen && !inline) {
        return (
            <div
                className="hidden md:flex fixed right-0 z-[300] flex-col items-center gap-0.5"
                style={{
                    top: `${fabTopVh}vh`,
                    transform: 'translateY(-50%)',
                }}
            >
                <button
                    type="button"
                    title="Sleep om positie te wijzigen"
                    aria-label="Sleep TO DO-knop"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setFabDragging(true);
                    }}
                    className="rounded-l-md bg-slate-700/90 px-1 py-1 text-slate-200 hover:bg-slate-600 cursor-grab active:cursor-grabbing"
                >
                    <GripVertical size={14} />
                </button>
                <button
                    type="button"
                    onClick={() => onToggle()}
                    className="bg-slate-900 text-white py-6 px-2 rounded-l-xl shadow-xl hover:bg-black transition-all hover:pr-4 flex items-center justify-center select-none"
                    style={{ writingMode: 'vertical-rl' }}
                >
                    <div className="flex items-center gap-2 rotate-180">
                        <span className="text-xs font-black uppercase tracking-widest">TO DO</span>
                        <CheckCircle2 size={16} className="rotate-180" />
                    </div>
                </button>
            </div>
        );
    }
    return (
        <>
            <div className={inline ? "flex flex-col h-full bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden" : "fixed inset-y-0 right-0 z-[300] w-80 bg-white shadow-2xl border-l border-slate-200 flex flex-col animate-slideInRight"}>
                {/* Header */}
                <div className={inline ? "p-6 border-b border-slate-100 bg-white flex justify-between items-center" : "p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50"}>
                    <div>
                        <h3 className={inline ? "text-xl font-black text-slate-900 tracking-tight" : "text-base font-black text-slate-900 uppercase tracking-tight"}>
                            {inline ? "Mijn Actiepunten" : "Mijn Taken"}
                        </h3>
                        <p className={inline ? "text-xs font-bold text-slate-400 mt-1 uppercase tracking-widest" : "text-[11px] text-slate-500 font-medium"}>
                            {inline ? "Wat staat er vandaag op de planning?" : "Beheer je todo's"}
                        </p>
                    </div>
                    {!inline && <button onClick={onToggle} className="p-1.5 hover:bg-slate-200 rounded-full transition-colors"><X size={16} className="text-slate-400" /></button>}
                </div>

                {/* Filters */}
                <div className="p-3 border-b border-slate-100 flex gap-1.5 overflow-x-auto custom-scrollbar">
                    <FilterButton label="Alles" active={filter === 'all'} onClick={() => setFilter('all')} icon={<CheckCircle2 size={12} />} />
                    <FilterButton label="Mijn" active={filter === 'me'} onClick={() => setFilter('me')} icon={<User size={12} />} />
                    {vacancyId && <FilterButton label="Vacature" active={filter === 'vacancy'} onClick={() => setFilter('vacancy')} icon={<Briefcase size={12} />} />}
                    {candidateId && <FilterButton label="Kand." active={filter === 'candidate'} onClick={() => setFilter('candidate')} icon={<User size={12} />} />}
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#F8FAFC]">
                    {loading ? <div className="flex justify-center p-4"><Loader2 className="animate-spin text-slate-300" /></div> : (
                        todos.length === 0 ? (
                            <div className="text-center p-6 opacity-50">
                                <CheckCircle2 size={32} className="mx-auto text-slate-300 mb-2" />
                                <p className="text-xs font-bold text-slate-400">Geen taken gevonden</p>
                            </div>
                        ) : (
                            todos.map(todo => (
                                <div key={todo.id} className={`group bg-white border border-slate-200 rounded-lg p-2.5 flex items-start gap-2.5 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer ${todo.status === 'Completed' ? 'opacity-50 line-through bg-slate-50' : ''}`} onClick={() => setSelectedTodoId(todo.id)}>
                                    <button onClick={(e) => { e.stopPropagation(); toggleTodo(todo); }} className={`mt-0.5 shrink-0 ${todo.status === 'Completed' ? 'text-emerald-500' : 'text-slate-300 hover:text-indigo-500'}`}>
                                        {todo.status === 'Completed' ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[13px] font-bold text-slate-800 leading-tight truncate">{todo.title}</p>
                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {todo.priority === 'High' && <span className="text-[9px] bg-amber-50 text-amber-600 px-1 py-0.5 rounded border border-amber-100 font-bold flex items-center gap-0.5"><Flag size={8} /> High</span>}
                                            {todo.due_date && <span className={`text-[9px] border px-1 py-0.5 rounded font-bold flex items-center gap-0.5 ${new Date(todo.due_date) < new Date() && todo.status !== 'Completed' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-slate-50 text-slate-500 border-slate-100'}`}><Calendar size={8} /> {new Date(todo.due_date).toLocaleDateString()}</span>}
                                            {todo.is_external && <span className="text-[9px] bg-slate-100 text-slate-600 px-1 py-0.5 rounded font-bold uppercase border border-slate-200">Klant</span>}
                                            {(todo as any).assignee && <span className="text-[9px] bg-indigo-50 text-indigo-700 px-1 py-0.5 rounded border border-indigo-100 font-bold flex items-center gap-0.5"><User size={8} /> {(todo as any).assignee.name.split(' ')[0]}</span>}
                                        </div>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); deleteTodo(todo.id); }} className="text-slate-200 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))
                        )
                    )}
                </div>

                {/* Add New */}
                <div className="p-3 border-t border-slate-100 bg-white relative">
                    <form onSubmit={handleAddTodo} className="space-y-2">
                        <div className="relative">
                            <input
                                ref={inputRef}
                                type="text"
                                value={newTodoTitle}
                                onChange={handleInputChange}
                                onFocus={() => setIsExpanded(true)}
                                placeholder="Nieuwe taak..."
                                className="w-full px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-[13px] font-bold outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all placeholder:font-medium"
                            />
                        </div>

                        {isExpanded && (
                            <div className="space-y-2 animate-fadeIn bg-slate-50 p-2 rounded-lg border border-slate-100">
                                <div className="flex items-center gap-1">
                                    {/* Action Buttons */}
                                    <button type="button" onClick={toggleAssignDropdown} className={`p-1.5 rounded-md text-[11px] font-bold flex items-center gap-1 transition-colors border ${assignedUser ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                                        <User size={12} /> {assignedUser ? assignedUser.name.split(' ')[0] : 'Wijs toe'}
                                    </button>
                                    <button type="button" onClick={toggleDatePicker} className={`p-1.5 rounded-md text-[11px] font-bold flex items-center gap-1 transition-colors border ${newTodoDate ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                                        <Calendar size={12} /> {newTodoDate ? new Date(newTodoDate).toLocaleDateString() : 'Datum'}
                                    </button>
                                    <button type="button" onClick={() => setPriority(priority === 'Medium' ? 'High' : priority === 'High' ? 'Low' : 'Medium')} className={`p-1.5 rounded-md text-[11px] font-bold flex items-center gap-1 transition-colors border ${priority === 'High' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                                        <Flag size={12} /> {priority === 'High' ? 'Hoog' : priority === 'Medium' ? 'Normaal' : 'Laag'}
                                    </button>
                                </div>

                                {/* Dynamic Popups */}
                                {showAssignDropdown && (
                                    <div className="bg-white border border-slate-200 rounded-md shadow-sm max-h-32 overflow-y-auto mt-1 p-1">
                                        <button type="button" onClick={() => selectColleague(null)} className="w-full text-left px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-50 rounded">-- Niemand --</button>
                                        {colleagues.map(c => (
                                            <button type="button" key={c.id} onClick={() => selectColleague(c)} className="w-full text-left px-2 py-1 text-[11px] font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 rounded transition-colors flex items-center gap-1.5">
                                                <div className="w-4 h-4 rounded-full bg-slate-100 text-[8px] flex items-center justify-center font-black">{c.name.charAt(0)}</div>
                                                {c.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {showDatePicker && (
                                    <input
                                        type="date"
                                        value={newTodoDate}
                                        onChange={(e) => { setNewTodoDate(e.target.value); setShowDatePicker(false); }}
                                        className="w-full px-2 py-1 bg-white border border-slate-200 rounded-md text-[11px] text-slate-700 outline-none mt-1"
                                    />
                                )}

                                <textarea
                                    value={newTodoDescription}
                                    onChange={(e) => setNewTodoDescription(e.target.value)}
                                    placeholder="Omschrijving (optioneel)..."
                                    className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-md text-[11px] text-slate-600 outline-none focus:border-indigo-500 min-h-[50px] resize-none"
                                />

                                <div className="flex justify-between items-center pt-1 border-t border-slate-200/50">
                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 cursor-pointer hover:text-slate-800 transition-colors">
                                        <input type="checkbox" checked={isExternal} onChange={e => setIsExternal(e.target.checked)} className="rounded-sm border-slate-300 text-indigo-600 focus:ring-indigo-600 w-3 h-3" />
                                        Zichtbaar voor klant
                                    </label>
                                    <div className="flex gap-1.5">
                                        <button type="button" onClick={() => { setIsExpanded(false); setNewTodoTitle(''); }} className="text-[10px] font-bold text-slate-400 hover:text-slate-600 px-2">Annuleer</button>
                                        <button type="submit" disabled={!newTodoTitle.trim()} className="bg-indigo-600 text-white p-1.5 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50">
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
            {selectedTodoId && (
                <TodoDetailModal todoId={selectedTodoId} onClose={() => setSelectedTodoId(null)} onUpdate={fetchTodos} />
            )}
        </>
    );
};

const FilterButton = ({ label, active, onClick, icon }: any) => (
    <button
        onClick={onClick}
        className={`
         flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0
         ${active ? 'bg-slate-900 text-white shadow-md' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'}
      `}
    >
        {icon} {label}
    </button>
);

export default TodoWidget;
